import {
  addDaysTo,
  addMonthsTo,
  addYearsTo,
  getMonthSum,
  IDateDiff,
  setToMonthAndLastDay,
  today
} from '@mng/mng-ts-helpers';
import { isLastDayOfMonth } from 'date-fns';
import { UvAngebotData } from '../../uvangebot.model.types';

const UV_MIN_LAUFZEIT_KURZFRISTIG_MONATE = 1;
const UV_MIN_LAUFZEIT_MONATE = 36;

export const UV_STORNO_POLIZZE_HINWEIS_TEXT =
  'Dieser Versicherungsbeginn ist nur dann möglich, wenn eine Kopie der Stornopolizze des Vorversicherers übermittelt wird';

export function getUvMinVersicherungsBeginn(uvVpmsBerechtigung: number, basisDatum = today()): Date {
  if (uvVpmsBerechtigung >= 30) {
    return addMonthsTo(basisDatum, -6);
  } else {
    return basisDatum;
  }
}

export function getUvMaxVersicherungsBeginn(basis = new Date()): Date {
  return addMonthsTo(basis, 11);
}

export function getUvMaxVersicherungsEnde(versicherungsBeginn = new Date(), kurzfristigerTarif = false): Date {
  if (!kurzfristigerTarif) {
    let result = addDaysTo(addMonthsTo(addYearsTo(versicherungsBeginn, 10), 11), -1);
    if (!isLastDayOfMonth(result)) {
      // Versicherungsende muss der Monatsletzte sein
      result = setToMonthAndLastDay(result, result.getMonth() - 1);
    }
    return result;
  } else {
    return addDaysTo(addMonthsTo(versicherungsBeginn, 11), -1);
  }
}

export function getUvMinVersicherungsEnde(versicherungsBeginn = new Date(), kurzfristigerTarif = false): Date {
  if (!kurzfristigerTarif) {
    let result = addDaysTo(addYearsTo(versicherungsBeginn, 3), -1);
    if (!isLastDayOfMonth(result)) {
      // Versicherungsende muss der Monatsletzte sein
      result = setToMonthAndLastDay(result, result.getMonth());
    }
    return result;
  } else {
    return addDaysTo(addMonthsTo(versicherungsBeginn, 1), -1);
  }
}

export function isUvStornoPolizzeHinweisNotwendig(versicherungsBeginn: Date, stichtag = new Date()): boolean {
  if (versicherungsBeginn) {
    const in6Monaten = addMonthsTo(stichtag, 6);
    return versicherungsBeginn.getTime() > in6Monaten.getTime();
  } else {
    return false;
  }
}

export function checkUpdateUvStornoPolizzeHinweis(uvAngebotData: UvAngebotData, versicherungsBeginn: Date) {
  if (!uvAngebotData.tarifZusatzDaten) {
    uvAngebotData.tarifZusatzDaten = {};
  }
  if (isUvStornoPolizzeHinweisNotwendig(versicherungsBeginn)) {
    uvAngebotData.tarifZusatzDaten.stornoPolizzeText = UV_STORNO_POLIZZE_HINWEIS_TEXT;
  } else {
    uvAngebotData.tarifZusatzDaten.stornoPolizzeText = undefined;
  }
}

export function isMinLaufzeitOk(laufzeit: IDateDiff, kurzfristigerTarif = false): boolean {
  const laufzeitMonate = getMonthSum(laufzeit);
  const minLaufzeit = kurzfristigerTarif ? UV_MIN_LAUFZEIT_KURZFRISTIG_MONATE : UV_MIN_LAUFZEIT_MONATE;
  return laufzeitMonate >= minLaufzeit;
}

export function getMinLaufzeitText(kurzfristigerTarif = false): string {
  return kurzfristigerTarif ? `${UV_MIN_LAUFZEIT_KURZFRISTIG_MONATE} Monat` : `${UV_MIN_LAUFZEIT_MONATE / 12} Jahre`;
}

export function isVersicherungsEndeMonatsLetzterFehler(versicherungsEnde: Date, kurzfristigerTarif): boolean {
  if (kurzfristigerTarif) {
    // Versicherungsende bei kurzfristgem Tarif ist beliebig
    return false;
  } else {
    // ansonsten muss das Ende ein Monatsletzter sein
    return !isLastDayOfMonth(versicherungsEnde);
  }
}

function hasUvLohnsteuervorteilKonto(uvAngebotData: UvAngebotData): boolean {
  return /UV\s+3\/1\/15\s+(BU|ZZ)$/.test(uvAngebotData?.fzKonto?.hinweis);
}

function hasUvJuristischePersonAsVn(uvAngebotData: UvAngebotData): boolean {
  return (uvAngebotData?.uvPersonenData?.personen ?? []).some(
    (p) => p.personRollen?.vn && p.stammdaten?.juristischePerson
  );
}

export function isUvLohnsteuervorteilCase(uvAngebotData: UvAngebotData): boolean {
  return hasUvLohnsteuervorteilKonto(uvAngebotData) && !hasUvJuristischePersonAsVn(uvAngebotData);
}

export function getFzKontoFirma(uvAngebotData: UvAngebotData): string {
  return uvAngebotData?.fzKonto?.kontoName || '';
}
